import { Renderer } from "@with-nx/cms-ui";
import { Rule } from "@with-nx/simple-ui/atoms";
import { ReusableSection } from "@with-nx/simple-ui/atoms";
import { CatalogLayout } from "@with-nx/simple-ui/organisms";
import { Col, Row } from "antd";

import LegalMenuDesktop from "../../components/w/LegalMenuDesktop";

export default function Page() {
  return (
    <CatalogLayout
      footerNoAngle
      title="Broadway Media - Payment"
      description="This Payment document was last updated on January 17, 2024"
    >
      <ReusableSection>
        <div className="container mx-auto max-w-7xl">
          <Row gutter={[36, 18]} justify="space-between">
            <Col xs={24} sm={24} md={24} lg={6} xl={6}>
              <LegalMenuDesktop />
            </Col>
            <Col xs={24} sm={24} md={24} lg={18} xl={18}>
              <Rule rule="hs" color="var(--font1)" display="block" bottom={24}>
                Payment Agreement
              </Rule>
              <Rule rule="ls" color="var(--font3)" display="block" bottom={24}>
                This Payment document was last updated on January 17, 2024
              </Rule>
              <Renderer
                data={{
                  blocks: [
                    {
                      type: "paragraph",
                      data: {
                        text: `This Agreement is subject to the <a rel="noreferrer" href="/legal/cancellation-policy">Cancellation Policy</a> and <a rel="noreferrer" href="/legal/subscription-service-agreement">Subscription Service Agreement</a>, which in any conflict matters, in all instances overrides this Payment Agreement.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `At Broadway Media, we strive to provide exceptional services to our valued customers. As part of our commitment to transparency and ensuring a smooth payment experience, we have developed this Payment Policy to outline the financial terms and conditions that govern the use of our free and paid services. Whether you're interested in accessing exclusive content or utilizing premium features, this policy will guide you through the payment process and clarify important details regarding subscriptions, one-time payments, refunds, and more.`,
                      },
                    },
                    {
                      type: "bold",
                      data: {
                        text: `General`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `1.1 We offer both free and paid Services. Certain features of or content available through the Services require payment in the form of a one-time payment ("One-Time Payment") or by purchasing a subscription ("Subscription").`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `1.2 By making a payment or purchasing a subscription, you expressly authorize us (or our third-party payment processor) to charge you for the transaction.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `1.3 Payment Information: You may be required to provide additional information relevant to your transaction, including credit card details and billing addresses. You represent and warrant that you have the legal right to use the provided payment method(s).`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `1.4 Pricing and Billing: By initiating a transaction, you agree to the pricing, payment, and billing policies applicable to the fees and charges, as communicated to you.`,
                      },
                    },
                    {
                      type: "bold",
                      data: {
                        text: `Subscriptions`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `2.1 If you purchase a Subscription, you will be charged the monthly Subscription Fee or the fee for the applicable Subscription period indicated through the Services, plus any applicable taxes and other charges.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `2.2 Recurring Payments: By purchasing a Subscription, you authorize us to initiate recurring non-refundable payments using the provided Payment Information until you cancel your Subscription.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `2.3 Responsibility for Payments: You accept responsibility for all recurring payment obligations prior to the cancellation of your Subscription by you or us.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `2.4 Cancellation: You can cancel your Subscription at any time. However, cancellation will be effective at the end of the then-current Subscription period, and you will not receive a refund for any portion of the Subscription Fee paid for the current Subscription period.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `2.5 Cancellation Deadline: To avoid being charged for the subsequent Subscription period, you must cancel your Subscription at least twenty-four (24) hours before the end of your current Subscription period.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `2.6 Continued Service: If you cancel, your right to use the Services will continue until the end of your current subscription period and will then terminate without further charges.`,
                      },
                    },
                    {
                      type: "bold",
                      data: {
                        text: `One-Time Payments and Refunds`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `3.1 One-Time Payment Refunds: Your One-Time Payment or Subscription Fee is non-refundable, except if we cancel your Transaction, in which case we will refund any payment you have already remitted to us.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `3.2 Disputed Charges: If you dispute any charges, you must notify us within sixty (60) days after the charge was incurred.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `3.3 Price Changes: We reserve the right to change our prices in the future, and we may change the Subscription Fees upon advance notice to you. Your continued use of the Services after the price change goes into effect constitutes your agreement to pay the changed amount.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `3.4 Past Due Fees: Past due fees are subject to a finance charge of 1.5% per month on any outstanding balance, or the maximum permitted by law, whichever is lower, plus all expenses of collection.`,
                      },
                    },
                    {
                      type: "bold",
                      data: {
                        text: `Order Refusal and Limitations`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `4.1 Order Refusal: We reserve the right to refuse any order you place with us.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `4.2 Quantity Limitations: We may, at our sole discretion, limit or cancel quantities purchased per person, per household, or per order.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `4.3 Order Change or Cancellation: In the event that we make a change to or cancel an order, we will attempt to notify you using the contact information provided at the time of the order.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `4.4 Prohibited Orders: We may limit or prohibit orders that, in our sole judgment, appear to be placed by dealers, resellers, or distributors.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `4.5 Account Information: You agree to provide current, complete, and accurate purchase and account information. It is your responsibility to update your account and other information promptly.`,
                      },
                    },
                    {
                      type: "bold",
                      data: {
                        text: `Modifications to Terms and Conditions`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `5.1 We reserve the right to change, modify, add, or remove portions of the Terms and Conditions of any of our products at any time, to the extent allowed by applicable law.`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `5.2 Changes to Terms: Please check these terms periodically for changes. Your continued use of the Services following the posting of changes to these terms will indicate your acceptance of those changes.`,
                      },
                    },
                    {
                      type: "bold",
                      data: {
                        text: `Refunds`,
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `At Broadway Media Distribution, Inc., we strive to ensure customer satisfaction. Please review our credit card refund policy below:`,
                      },
                    },
                    {
                      type: "list",
                      data: {
                        text: [
                          `Eligibility: Refunds are applicable for purchases made using a credit or debit card.`,
                          `Valid for transactions made by USA or Canada customers.`,
                          `Refund Timeframe: Refund requests must be submitted within 2 business days of the original transaction. Approval within this timeframe may result in a credit card refund; otherwise, refunds will be processed via check.`,
                          `Processing Fees: A 3.5% plus $0.50 processing fee, incurred by credit card processors, applies to any credit card transaction refund. The remaining amount will be sent as the refund to the customer.`,
                          `Check Refunds: Purchases made by check from USA customers incur no additional refund costs. Refunds for check payments will be issued via check.`,
                          `International Payments: For purchases made outside USD or any payments from USA or Canada customers, refunds are processed via wire transfer. A 1% wire transfer fee applies to the refunded amount.`,
                          `Credit Option: Customers have the option to forgo a refund and, instead, receive the full value of their purchase as a credit on their account.`,
                        ],
                      },
                    },
                    {
                      type: "paragraph",
                      data: {
                        text: `Please note that this payment policy is subject to change, and any updates will be communicated to you as necessary.`,
                      },
                    },
                  ],
                }}
              />
            </Col>
          </Row>
        </div>
      </ReusableSection>
    </CatalogLayout>
  );
}
